import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Home, Network, ArrowRight, ArrowLeft, Save, Building2, LayoutGrid, Check, Plus, Trash2, GripVertical, X, Tag } from 'lucide-react';
import { CustomBreadcrumb, CustomInput, CustomButton, CustomSelect, CustomSearchableSelect } from '@/shared/components/atoms';
import { CustomCommonScreenHeader, ConfirmationModal } from '@/shared/components/molecules';
import { useAuthStore } from '@/shared/stores/authStore';
import { useToastStore } from '@/shared/stores/toastStore';
import { departmentApi } from '../api/departmentApi';
import { tagApi } from '../api/tagApi';
import { Department, Tag as TagType } from '../types';

const workflowSchema = z.object({
    department_id: z.string().min(1, 'Department is required'),
    name: z.string().min(2, 'Workflow name is required'),
    description: z.string().min(5, 'Description is required (min 5 chars)'),
    tag_ids: z.array(z.string()).min(1, 'At least one tag is required')
});

type WorkflowFormData = z.infer<typeof workflowSchema>;

import { workflowApi } from '../api/workflowApi';

export const CreateWorkflow = () => {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const { showToast } = useToastStore();
    const schoolSlug = user?.school?.slug || '';

    const [departments, setDepartments] = useState<Department[]>([]);
    const [tagsList, setTagsList] = useState<TagType[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Post-creation modal state
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [createdWorkflowId, setCreatedWorkflowId] = useState<string | null>(null);

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        getValues,
        formState: { errors }
    } = useForm<WorkflowFormData>({
        resolver: zodResolver(workflowSchema),
        defaultValues: { tag_ids: [], department_id: '' }
    });

    const selectedTags = watch('tag_ids') || [];

    useEffect(() => {
        if (schoolSlug) {
            fetchInitialData();
        }
    }, [schoolSlug]);

    const fetchInitialData = async () => {
        setIsLoading(true);
        const [deptRes, tagsRes] = await Promise.all([
            departmentApi.getDepartments(schoolSlug, { page: 1, page_size: 50 }),
            tagApi.getTags(schoolSlug, { page: 1, page_size: 50 })
        ]);

        if (deptRes.success && deptRes.data) {
            const data = deptRes.data as any;
            setDepartments(Array.isArray(data) ? data : (data.items || []));
        }
        if (tagsRes.success && tagsRes.data) {
            const data = tagsRes.data as any;
            setTagsList(Array.isArray(data) ? data : (data.items || []));
        }

        setIsLoading(false);
    };

    const toggleTag = (id: string) => {
        if (selectedTags.includes(id)) {
            setValue('tag_ids', selectedTags.filter(t => t !== id), { shouldValidate: true });
        } else {
            setValue('tag_ids', [...selectedTags, id], { shouldValidate: true });
        }
    };

    const onSubmit = async (data: WorkflowFormData) => {
        if (!schoolSlug) return;
        setIsSubmitting(true);

        const payload = {
            department_id: data.department_id,
            name: data.name,
            description: data.description,
            tag_ids: data.tag_ids || []
        };

        const response = await workflowApi.createWorkflow(schoolSlug, payload);

        if (response.success && response.data) {
            showToast('Workflow created successfully', 'success');
            setCreatedWorkflowId(response.data.id);
            setShowConfigModal(true);
        } else {
            showToast(response.error || 'Failed to create workflow', 'error');
        }
        setIsSubmitting(false);
    };

    const handleSkipConfig = () => {
        setShowConfigModal(false);
        navigate(`/${schoolSlug}/school-admin/workflows`);
    };

    const handleConfigureLevels = () => {
        if (createdWorkflowId) {
            setShowConfigModal(false);
            const deptId = getValues('department_id');
            navigate(`/${schoolSlug}/school-admin/departments/${deptId}/sub-departments/${createdWorkflowId}/levels`);
        }
    };



    return (
        <div style={{ padding: '1rem 1.5rem' }}>
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'Workflows', path: `/${schoolSlug}/school-admin/workflows`, icon: <Network size={14} /> },
                    { label: 'Create Workflow' }
                ]}
            />

            <CustomCommonScreenHeader
                title="Create New Workflow"
                subtitle="Design a custom routing and approval process for your departments."
                icon={<Network size={24} />}
            />

            <div style={{
                maxWidth: '800px',
                margin: '2rem auto',
                background: 'var(--card-bg)',
                borderRadius: '16px',
                border: '1px solid var(--border-color)',
                padding: '2.5rem',
                boxShadow: 'var(--card-shadow)'
            }}>
                <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>

                    <div>
                        <CustomInput
                            label="Workflow Name"
                            placeholder="e.g. Leave Request Flow"
                            error={errors.name?.message}
                            {...register('name')}
                            required
                        />
                    </div>
                    <div>
                        <CustomInput
                            label="Description"
                            placeholder="Briefly describe the purpose of this workflow"
                            error={errors.description?.message}
                            {...register('description')}
                            required
                        />
                    </div>
                    <div>
                        <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block' }}>
                            Assign Department <span style={{ color: 'var(--status-error)' }}>*</span>
                        </label>
                        <CustomSearchableSelect
                            options={departments.map(d => ({ label: d.name, value: d.id }))}
                            value={watch('department_id') || ''}
                            onChange={(val) => setValue('department_id', val as string, { shouldValidate: true })}
                            placeholder="Search and select department..."
                            leftIcon={<Building2 size={18} />}
                            hideAvatar
                        />
                        {errors.department_id && <p style={{ color: 'var(--status-error)', fontSize: '0.75rem', marginTop: '0.5rem' }}>{errors.department_id.message}</p>}
                    </div>

                    <div>
                        <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block' }}>
                            Associated Tags <span style={{ color: 'var(--status-error)' }}>*</span>
                        </label>
                        
                        {/* Selected Tag Capsules */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: selectedTags.length > 0 ? '1rem' : '0' }}>
                            {selectedTags.map(id => {
                                const tag = tagsList.find(t => t.id === id);
                                if (!tag) return null;
                                return (
                                    <div
                                        key={id}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.4rem',
                                            padding: '0.35rem 0.75rem',
                                            background: 'var(--accent-primary)',
                                            color: 'white',
                                            borderRadius: '16px',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            boxShadow: '0 2px 4px rgba(99, 102, 241, 0.2)'
                                        }}
                                    >
                                        <Tag size={12} />
                                        {tag.name}
                                        <X 
                                            size={14} 
                                            style={{ cursor: 'pointer', marginLeft: '0.2rem' }} 
                                            onClick={() => toggleTag(id)}
                                        />
                                    </div>
                                );
                            })}
                        </div>

                        {/* Searchable Tag Select */}
                        <CustomSearchableSelect
                            options={tagsList
                                .filter(t => !selectedTags.includes(t.id))
                                .map(t => ({ label: t.name, value: t.id }))}
                            onChange={(val) => toggleTag(val as string)}
                            placeholder="Search and select tags..."
                            leftIcon={<Tag size={18} />}
                            hideAvatar
                        />
                        {errors.tag_ids && <p style={{ color: 'var(--status-error)', fontSize: '0.75rem', marginTop: '0.5rem' }}>{errors.tag_ids.message}</p>}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem', borderTop: '1px solid var(--border-color)', paddingTop: '2rem' }}>
                        <CustomButton type="button" variant="outline" color="secondary" onClick={() => navigate(`/${schoolSlug}/school-admin/workflows`)} disabled={isSubmitting} style={{ marginRight: '1rem' }}>
                            Cancel
                        </CustomButton>
                        <CustomButton type="submit" variant="filled" color="primary" loading={isSubmitting}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Save size={16} /> Save Workflow
                            </div>
                        </CustomButton>
                    </div>
                </form>
            </div>

            <ConfirmationModal
                isOpen={showConfigModal}
                onClose={handleSkipConfig}
                onConfirm={handleConfigureLevels}
                title="Configure Workflow"
                message="Workflow created successfully! Would you like to configure the approval levels and routing steps for this workflow now?"
                confirmLabel="Configure Levels"
                cancelLabel="Skip for now"
                variant="info"
            />
        </div>
    );
};
